import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StoreTable from './components/StoreTable';
import StoreDetailPanel from './components/StoreDetailPanel';
import StoreFormModal from './components/StoreFormModal';
import BulkActionsPanel from './components/BulkActionsPanel';
import StoreLayoutTab from './components/StoreLayoutTab';

const StoreManagement = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStores, setSelectedStores] = useState([]);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingStore, setEditingStore] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [activeTab, setActiveTab] = useState('stores');

  // Mock data for stores
  const mockStores = [
    {
      id: 'STR001',
      name: 'Dhanmondi Flagship Store',
      location: 'Dhaka',
      address: 'House 12, Road 7, Dhanmondi, Dhaka-1205',
      size: 1200,
      monthlyRent: 80000,
      rentPerSqFt: 66.67,
      contactPerson: 'Ahmed Rahman',
      phone: '01712345678',
      email: 'ahmed.rahman@brandstore.com',
      hasCurrentLayout: true,
      createdAt: '2024-08-01',
      lastUpdated: '2024-09-28'
    },
    {
      id: 'STR002',
      name: 'Gulshan Premium Outlet',
      location: 'Dhaka',
      address: 'Plot 45, Road 11, Gulshan-2, Dhaka-1212',
      size: 800,
      monthlyRent: 75000,
      rentPerSqFt: 93.75,
      contactPerson: 'Fatima Khan',
      phone: '01798765432',
      email: 'fatima.khan@brandstore.com',
      hasCurrentLayout: true,
      createdAt: '2024-07-15',
      lastUpdated: '2024-09-25'
    },
    {
      id: 'STR003',
      name: 'Chittagong Central Store',
      location: 'Chittagong',
      address: '123 Agrabad Commercial Area, Chittagong-4100',
      size: 950,
      monthlyRent: 55000,
      rentPerSqFt: 57.89,
      contactPerson: 'Mohammad Ali',
      phone: '01634567890',
      email: 'mohammad.ali@brandstore.com',
      hasCurrentLayout: false,
      createdAt: '2024-06-20',
      lastUpdated: '2024-09-20'
    },
    {
      id: 'STR004',
      name: 'Sylhet Shopping Complex',
      location: 'Sylhet',
      address: 'Zindabazar Shopping Complex, Sylhet-3100',
      size: 600,
      monthlyRent: 35000,
      rentPerSqFt: 58.33,
      contactPerson: 'Rashida Begum',
      phone: '01556789012',
      email: 'rashida.begum@brandstore.com',
      hasCurrentLayout: true,
      createdAt: '2024-05-10',
      lastUpdated: '2024-09-18'
    },
    {
      id: 'STR005',
      name: 'Rajshahi Market Hub',
      location: 'Rajshahi',
      address: 'Saheb Bazar Road, Rajshahi-6000',
      size: 750,
      monthlyRent: 42000,
      rentPerSqFt: 56.00,
      contactPerson: 'Karim Uddin',
      phone: '01445678901',
      email: 'karim.uddin@brandstore.com',
      hasCurrentLayout: false,
      createdAt: '2024-04-25',
      lastUpdated: '2024-09-15'
    },
    {
      id: 'STR006',
      name: 'Khulna Trade Center',
      location: 'Khulna',
      address: 'KDA Avenue, Khulna-9000',
      size: 850,
      monthlyRent: 48000,
      rentPerSqFt: 56.47,
      contactPerson: 'Nasir Ahmed',
      phone: '01723456789',
      email: 'nasir.ahmed@brandstore.com',
      hasCurrentLayout: true,
      createdAt: '2024-03-12',
      lastUpdated: '2024-09-12'
    }
  ];

  useEffect(() => {
    // Simulate loading data
    const loadStores = async () => {
      setIsLoading(true);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setStores(mockStores);
      setIsLoading(false);
    };

    loadStores();
  }, []);

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    setShowDetailPanel(true);
  };

  const handleAddStore = () => {
    setFormMode('create');
    setEditingStore(null);
    setIsFormModalOpen(true);
  };

  const handleEditStore = (store) => {
    setFormMode('edit');
    setEditingStore(store);
    setIsFormModalOpen(true);
  };

  const handleDeleteStore = (store) => {
    if (window.confirm(`Are you sure you want to delete "${store?.name}"? This action cannot be undone.`)) {
      setStores(prev => prev?.filter(s => s?.id !== store?.id));
      if (selectedStore?.id === store?.id) {
        setSelectedStore(null);
        setShowDetailPanel(false);
      }
    }
  };

  const handleFormSubmit = async (storeData) => {
    if (formMode === 'create') {
      const newStore = {
        ...storeData,
        id: `STR${String(stores?.length + 1)?.padStart(3, '0')}`,
        hasCurrentLayout: false,
        createdAt: new Date()?.toISOString()?.split('T')?.[0],
        lastUpdated: new Date()?.toISOString()?.split('T')?.[0]
      };
      setStores(prev => [...prev, newStore]);
    } else {
      setStores(prev => prev?.map(store => 
        store?.id === editingStore?.id 
          ? { ...store, ...storeData, lastUpdated: new Date()?.toISOString()?.split('T')?.[0] }
          : store
      ));
      if (selectedStore?.id === editingStore?.id) {
        setSelectedStore(prev => ({ ...prev, ...storeData }));
      }
    }
  };

  const handleBulkUpdate = async (action, data) => {
    switch (action) {
      case 'rent':
        setStores(prev => prev?.map(store => {
          const updatedStore = data?.find(s => s?.id === store?.id);
          return updatedStore || store;
        }));
        break;
      case 'location':
        setStores(prev => prev?.map(store => {
          const updatedStore = data?.find(s => s?.id === store?.id);
          return updatedStore || store;
        }));
        break;
      case 'delete':
        setStores(prev => prev?.filter(store => !data?.includes(store?.id)));
        if (selectedStore && data?.includes(selectedStore?.id)) {
          setSelectedStore(null);
          setShowDetailPanel(false);
        }
        break;
      default:
        break;
    }
    setSelectedStores([]);
  };

  const handleNavigateToLayouts = (store) => {
    navigate('/store-layout-manager', { state: { storeId: store?.id } });
  };

  const handleNavigateToBranding = (store) => {
    navigate('/branding-cost-calculator', { state: { storeId: store?.id } });
  };

  const handleCloseDetailPanel = () => {
    setShowDetailPanel(false);
    setSelectedStore(null);
  };

  const tabs = [
    { id: 'stores', label: 'Stores', icon: 'Store' },
    { id: 'layouts', label: 'Layouts', icon: 'Layout' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading stores...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumbs */}
        <Breadcrumbs />

        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Store Management</h1>
            <p className="text-muted-foreground mt-1">
              Manage store information, layouts, and calculate branding costs across all locations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={() => {
                const csvContent = stores?.map(store => 
                  `${store?.id},${store?.name},${store?.location},${store?.size},${store?.monthlyRent},${store?.rentPerSqFt?.toFixed(2)}`
                )?.join('\n');
                const blob = new Blob([`ID,Name,Location,Size,Monthly Rent,Rent per Sq Ft\n${csvContent}`], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'stores_export.csv';
                a?.click();
              }}
            >
              Export Data
            </Button>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddStore}
            >
              Add Store
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex overflow-x-auto">
            {tabs?.map((tab) => (
              <Button
                key={tab?.id}
                variant="ghost"
                onClick={() => setActiveTab(tab?.id)}
                className={`flex-shrink-0 px-6 py-3 rounded-none border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab?.icon} size={16} className="mr-2" />
                {tab?.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'stores' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-card p-4 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Store" size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">{stores?.length}</div>
                    <div className="text-sm text-muted-foreground">Total Stores</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-4 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} className="text-success" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">
                      {stores?.filter(s => s?.hasCurrentLayout)?.length}
                    </div>
                    <div className="text-sm text-muted-foreground">With Layouts</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-4 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="TrendingUp" size={20} className="text-accent" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">
                      ৳{Math.round(stores?.reduce((sum, s) => sum + s?.rentPerSqFt, 0) / stores?.length)?.toLocaleString('bn-BD')}
                    </div>
                    <div className="text-sm text-muted-foreground">Avg Rent/Sq Ft</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-4 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                    <Icon name="MapPin" size={20} className="text-secondary" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">
                      {new Set(stores.map(s => s.location))?.size}
                    </div>
                    <div className="text-sm text-muted-foreground">Locations</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bulk Actions Panel */}
            <BulkActionsPanel
              selectedStores={selectedStores}
              onBulkUpdate={handleBulkUpdate}
              onClearSelection={() => setSelectedStores([])}
              stores={stores}
            />

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Store Table */}
              <div className={`${showDetailPanel ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
                <StoreTable
                  stores={stores}
                  onStoreSelect={handleStoreSelect}
                  selectedStore={selectedStore}
                  onEditStore={handleEditStore}
                  onDeleteStore={handleDeleteStore}
                />
              </div>

              {/* Detail Panel */}
              {showDetailPanel && (
                <div className="lg:col-span-1">
                  <StoreDetailPanel
                    store={selectedStore}
                    onClose={handleCloseDetailPanel}
                    onEdit={handleEditStore}
                    onNavigateToLayouts={handleNavigateToLayouts}
                    onNavigateToBranding={handleNavigateToBranding}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'layouts' && (
          <StoreLayoutTab 
            stores={stores}
            onStoreUpdate={(updatedStore) => {
              setStores(prev => prev?.map(store => 
                store?.id === updatedStore?.id ? updatedStore : store
              ));
              if (selectedStore?.id === updatedStore?.id) {
                setSelectedStore(updatedStore);
              }
            }}
          />
        )}

        {/* Store Form Modal */}
        <StoreFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onSubmit={handleFormSubmit}
          store={editingStore}
          mode={formMode}
        />

        {/* Quick Action Button for Mobile */}
        <QuickActionButton />
      </main>
    </div>
  );
};

export default StoreManagement;